import { i18n } from "@lingui/core"

function toUTCDate(date: Date | string): Date {
    if (typeof date === "string") date = new Date(date)

    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), 0, 0))
}

function toUTCDateTime(date: Date): Date {
    return new Date(
        Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds(),
        ),
    )
}

export type FormatDateFormat = {
    day?: "numeric" | "2-digit"
    weekday?: "long" | "short" | "narrow"
    month?: "long" | "short" | "narrow"
    year?: "numeric" | "2-digit"
    hour?: "numeric" | "2-digit" | undefined
    minute?: "numeric" | "2-digit" | undefined
    second?: "numeric" | "2-digit" | undefined
}

function formatDate(date: undefined, format: FormatDateFormat): string
function formatDate(date: Date | string, format: FormatDateFormat): string
function formatDate(date: Date | string | undefined, format: FormatDateFormat): string {
    if (!date) return ""
    const dateFormat = { ...format, timeZone: "UTC" }

    const utcDate = toUTCDate(date)
    return i18n.date(utcDate, dateFormat)
}

export function formatLocalDate(date: Date, format: FormatDateFormat): string {
    const localFormat = { ...format, timeZone: undefined }
    const dateTimeFormat = new Intl.DateTimeFormat(i18n.locale, localFormat)
    return dateTimeFormat.format(date)
}

// NOTE: taken from here https://stackoverflow.com/a/57842203
const RES_TIMEZONE = "America/New_York"
export function forceResTimezone(input: Date) {
    const date = toUTCDateTime(input)

    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }))
    const resDate = new Date(date.toLocaleString("en-US", { timeZone: RES_TIMEZONE }))
    const offset = utcDate.getTime() - resDate.getTime()

    date.setTime(date.getTime() + offset)

    return date
}

function formatDateRange(startDate: Date, endDate: Date, format: FormatDateFormat): string {
    const dateTimeFormat = new Intl.DateTimeFormat(i18n.locale, {
        ...format,
        calendar: "gregory",
        timeZone: "UTC",
    })

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (dateTimeFormat as any).formatRange(startDate, endDate)
    } catch (e) {
        return `${formatDate(startDate, format)} - ${formatDate(endDate, format)}`
    }
}

function formatCruiseArrivalDepartureDate(departureDate: Date, arrivalDate: Date): string {
    const format: FormatDateFormat = {
        year: "numeric",
        month: "short",
        day: "numeric",
    }
    const value = formatDateRange(departureDate, arrivalDate, format)
    return value.replace("–", "→")
}

export const longDateTimeWeekDayFormat: FormatDateFormat = {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "long",
}

export { toUTCDate, formatDate, formatCruiseArrivalDepartureDate }
